import React, { useState, useRef, useEffect } from 'react';
import { Upload, Clipboard, Check, X, FileSpreadsheet, Eye } from 'lucide-react';
import { TableBlockData } from '../../../core/ast/document.types';
import { parseDelimitedTextToTable, parseExcelOrCsvFile } from '../../../core/ast/tableParser';

interface TableImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (tableData: Partial<TableBlockData>) => void;
}

export const TableImportModal: React.FC<TableImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [activeTab, setActiveTab] = useState<'paste' | 'upload'>('paste');
  const [pastedText, setPastedText] = useState('');
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [selectedDelimiter, setSelectedDelimiter] = useState<string>('auto');
  const [parsedPreview, setParsedPreview] = useState<TableBlockData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [workbookTables, setWorkbookTables] = useState<Record<string, TableBlockData>>({});
  const [isLoading, setIsLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview when pasted text or settings change
  useEffect(() => {
    if (activeTab === 'paste') {
      if (!pastedText.trim()) {
        setParsedPreview(null);
        setErrorMsg(null);
        return;
      }
      try {
        const table = parseDelimitedTextToTable(pastedText, {
          hasHeaderRow,
          customDelimiter: selectedDelimiter === 'auto' ? undefined : selectedDelimiter,
        });
        setParsedPreview(table);
        setErrorMsg(null);
      } catch (err: any) {
        setErrorMsg(err?.message || 'Failed to parse table data');
        setParsedPreview(null);
      }
    }
  }, [pastedText, hasHeaderRow, selectedDelimiter, activeTab]);

  if (!isOpen) return null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setErrorMsg(null);

    try {
      const result = await parseExcelOrCsvFile(file);
      setSheetNames(result.sheetNames);
      setSelectedSheet(result.sheetNames[0] || '');
      setWorkbookTables(result.tables);
      setParsedPreview(result.defaultTable);
    } catch (err: any) {
      setErrorMsg(`Failed to read spreadsheet: ${err?.message || 'Invalid format'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (workbookTables[sheetName]) {
      setParsedPreview(workbookTables[sheetName]);
    }
  };

  const handleApply = () => {
    if (!parsedPreview) return;
    onImport(parsedPreview);
    onClose();
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 1050 }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '780px', width: '92%' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FileSpreadsheet size={20} color="var(--primary-600)" />
            <h3 className="modal-title" style={{ margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
              Import Data & Spreadsheets
            </h3>
          </div>
          <button type="button" className="btn-icon" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'paste' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('paste')}
            >
              <Clipboard size={14} /> Paste from Excel / Google Sheets
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'upload' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('upload')}
            >
              <Upload size={14} /> Upload Spreadsheet (.xlsx, .csv, .tsv)
            </button>
          </div>

          {/* Paste Tab Content */}
          {activeTab === 'paste' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Paste rows/columns directly from Excel, Sheets, or CSV:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <label style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={hasHeaderRow}
                      onChange={(e) => setHasHeaderRow(e.target.checked)}
                    />
                    First row as header
                  </label>
                  <select
                    className="form-select text-xs"
                    style={{ width: 'auto', padding: '2px 8px' }}
                    value={selectedDelimiter}
                    onChange={(e) => setSelectedDelimiter(e.target.value)}
                  >
                    <option value="auto">Auto-detect delimiter</option>
                    <option value="	">Tab (Excel / Sheets)</option>
                    <option value=",">Comma (CSV)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="|">Markdown Pipe (|)</option>
                  </select>
                </div>
              </div>

              <textarea
                className="form-textarea"
                rows={5}
                placeholder="Copy cells in Excel/Google Sheets (Ctrl+C) and paste here (Ctrl+V)...&#10;Example:&#10;Trial	Voltage (V)	Current (mA)	Resistance (Ω)&#10;1	0.72	8.60	83.72&#10;2	0.65	2.45	265.31"
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)' }}
                autoFocus
              />
            </div>
          )}

          {/* Upload Tab Content */}
          {activeTab === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-5)',
                  textAlign: 'center',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
              >
                <Upload size={32} color="var(--primary-600)" style={{ margin: '0 auto var(--space-2)' }} />
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  Click to upload or drag & drop Excel / CSV file
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  Supported formats: .xlsx, .xls, .csv, .tsv, .txt
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,.tsv,.txt"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </div>

              {sheetNames.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <label className="form-label" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
                    Select Worksheet:
                  </label>
                  <select
                    className="form-select text-xs"
                    value={selectedSheet}
                    onChange={(e) => handleSheetChange(e.target.value)}
                    style={{ width: 'auto' }}
                  >
                    {sheetNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Error display */}
          {errorMsg && (
            <div style={{ color: 'var(--danger-600)', fontSize: 'var(--font-size-xs)', padding: 'var(--space-2)', backgroundColor: 'var(--danger-50)', borderRadius: 'var(--radius-sm)' }}>
              {errorMsg}
            </div>
          )}

          {/* Preview section */}
          {parsedPreview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Eye size={14} color="var(--primary-600)" />
                  Parsed Table Preview ({parsedPreview.rows} rows × {parsedPreview.cols} cols)
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                  Header Row: {parsedPreview.hasHeaderRow ? 'Yes' : 'No'}
                </span>
              </div>

              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--font-size-xs)' }}>
                  <tbody>
                    {parsedPreview.cells.slice(0, 10).map((row, rIdx) => (
                      <tr key={rIdx} style={{ backgroundColor: rIdx === 0 && parsedPreview.hasHeaderRow ? 'var(--bg-surface-subtle)' : undefined }}>
                        {row.map((cell, cIdx) => (
                          <td
                            key={cell.id || cIdx}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid var(--border-subtle)',
                              fontWeight: rIdx === 0 && parsedPreview.hasHeaderRow ? 700 : 'normal',
                              textAlign: cell.alignment || 'left',
                            }}
                          >
                            {cell.content || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedPreview.cells.length > 10 && (
                  <div style={{ textAlign: 'center', padding: '4px', fontSize: '10px', color: 'var(--text-muted)', backgroundColor: 'var(--bg-surface-subtle)' }}>
                    + {parsedPreview.cells.length - 10} more rows...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!parsedPreview || parsedPreview.cells.length === 0 || isLoading}
            onClick={handleApply}
          >
            <Check size={14} /> Import to Table ({parsedPreview ? `${parsedPreview.rows} × ${parsedPreview.cols}` : '0'})
          </button>
        </div>
      </div>
    </div>
  );
};
